/**
 * SuggestionDrawer — the review drawer for one catalog suggestion (PIM Wave
 * P3 Task 4). Shows the seller's proposal, then gates Approve / Decline.
 *
 * There is no per-open detail GET endpoint for a single suggestion — the
 * list response already carries the full row, so the caller passes the
 * whole `SuggestionResponse` object in rather than an id + a query hook
 * (unlike Task 3's `ReviewDrawer`, which re-fetches a dedicated `/review`
 * endpoint). `suggestion` can transiently be `null` while this sheet's
 * close animation plays (the parent clears its selection immediately on
 * close, before the exit transition finishes) — the body renders a minimal
 * placeholder for that window, mirroring `ReviewDrawer`'s `isLoading ||
 * !review` guard.
 *
 * Decline and the category-create form are SIBLING `BottomSheet`s (toggled
 * via `actionMode`), never nested inside this drawer's own `BottomSheet` —
 * nested `BottomSheet`s cannot escape the parent's transformed panel (see
 * `attribute-fields/components/GroupManager.tsx`'s header comment). The
 * decline textarea bounds/behavior copy `RejectDialog.tsx` (min 5 / max 500
 * chars).
 *
 * Approving a `kind="field"`/`"option"` suggestion hands off to the P1
 * Field Library: it navigates there with the proposed name/type riding in
 * router `location.state` (`pimSuggestionPrefill`), which `FieldLibraryPage`
 * + `FieldFormDrawer` consume to seed the create-mode form (see those
 * files' PIM Wave P3 Task 4 comments). The suggestion is already `approved`
 * by the time the admin lands there — creating the field is a SEPARATE,
 * best-effort follow-up step, not part of the same transaction.
 */
import { useEffect, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { BottomSheet, Button, GPill } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { useIsDesktop } from "@/features/chat/useIsDesktop";
import { categoryDisplayName, useCategoryTree } from "@/features/marketplace/useCategoryTree";
import {
  suggestionKindTone,
  suggestionStatusTone,
  type SuggestionResponse,
} from "@/features/admin/suggestions/api";
import { useApproveSuggestion, useDeclineSuggestion } from "@/features/admin/suggestions/hooks";
import { ApproveCategoryDialog } from "@/features/admin/suggestions/components/ApproveCategoryDialog";

export interface SuggestionDrawerProps {
  suggestion: SuggestionResponse | null;
  open: boolean;
  onClose: () => void;
}

type ActionMode = "none" | "decline" | "approveCategory";

const MIN_REASON = 5;
const MAX_REASON = 500;

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function SuggestionDrawer({
  suggestion,
  open,
  onClose,
}: SuggestionDrawerProps): ReactElement {
  registerAdminI18n();
  const { t, i18n } = useTranslation(ADMIN_NS);
  const isDesktop = useIsDesktop();
  const navigate = useNavigate();
  const { findNode } = useCategoryTree();

  const approve = useApproveSuggestion();
  const decline = useDeclineSuggestion();

  const [actionMode, setActionMode] = useState<ActionMode>("none");
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!open) {
      setActionMode("none");
      setReason("");
    }
  }, [open]);

  const reasonValid = reason.length >= MIN_REASON && reason.length <= MAX_REASON;

  const handleDecline = async (): Promise<void> => {
    if (!suggestion || !reasonValid) return;
    try {
      await decline.mutateAsync({ id: suggestion.id, reason });
      toast.success(t("pim.suggestions.toast.declined"));
      setActionMode("none");
      onClose();
    } catch {
      toast.error(t("pim.suggestions.toast.error"));
    }
  };

  const handleApproveFieldOrOption = async (): Promise<void> => {
    if (!suggestion) return;
    try {
      await approve.mutateAsync({ id: suggestion.id, body: {} });
      toast.success(t("pim.suggestions.toast.approvedFieldHandoff"));
      onClose();
      // PIM Wave P3 Task 4 handoff — best-effort prefill (see
      // FieldLibraryPage.tsx / FieldFormDrawer.tsx). The suggestion is
      // already approved at this point regardless of whether the admin
      // completes field creation now or later.
      navigate("/admin/attribute-fields", {
        state: {
          pimSuggestionPrefill: {
            name: suggestion.payload.name,
            field_type: suggestion.payload.field_type ?? null,
          },
        },
      });
    } catch {
      toast.error(t("pim.suggestions.toast.error"));
    }
  };

  const handleApprove = (): void => {
    if (!suggestion) return;
    if (suggestion.kind === "category") {
      setActionMode("approveCategory");
    } else {
      void handleApproveFieldOrOption();
    }
  };

  const isPending = suggestion?.status === "pending";
  const parentCategoryId = suggestion?.payload.parent_category_id ?? null;
  const parentName = parentCategoryId
    ? (() => {
        const node = findNode(parentCategoryId);
        return node ? categoryDisplayName(node, i18n.language) : parentCategoryId;
      })()
    : null;

  const title = suggestion?.payload.name ?? t("pim.suggestions.title");

  return (
    <>
      <BottomSheet open={open} onClose={onClose} title={title} desktop={isDesktop}>
        {!suggestion ? (
          <div className="flex flex-col gap-3 py-2">
            <div className="h-4 w-2/3 animate-pulse rounded bg-[var(--g-elevated)]" />
            <div className="h-4 w-1/2 animate-pulse rounded bg-[var(--g-elevated)]" />
          </div>
        ) : (
          <div data-testid="suggestion-drawer-body" className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-2">
              <GPill tone={suggestionKindTone(suggestion.kind)} size="sm">
                {t(`pim.suggestions.kind.${suggestion.kind}`)}
              </GPill>
              <GPill tone={suggestionStatusTone(suggestion.status)} size="sm">
                {t(`pim.suggestions.status.${suggestion.status}`, {
                  defaultValue: suggestion.status,
                })}
              </GPill>
            </div>

            <dl className="divide-y divide-[var(--g-hairline)]">
              <div className="flex flex-col gap-0.5 py-1.5">
                <dt className="text-xs font-medium text-[var(--g-secondary)]">
                  {t("pim.suggestions.drawer.seller")}
                </dt>
                <dd className="text-sm">{suggestion.seller_id.slice(0, 8)}</dd>
              </div>

              {suggestion.payload.note ? (
                <div className="flex flex-col gap-0.5 py-1.5">
                  <dt className="text-xs font-medium text-[var(--g-secondary)]">
                    {t("pim.suggestions.drawer.note")}
                  </dt>
                  <dd className="whitespace-pre-wrap text-sm">{suggestion.payload.note}</dd>
                </div>
              ) : null}

              {suggestion.kind === "category" ? (
                <div className="flex flex-col gap-0.5 py-1.5">
                  <dt className="text-xs font-medium text-[var(--g-secondary)]">
                    {t("pim.suggestions.drawer.parent")}
                  </dt>
                  <dd className="text-sm">
                    {parentName ?? t("pim.suggestions.approveCategory.noParent")}
                  </dd>
                </div>
              ) : null}

              {suggestion.kind !== "category" && suggestion.payload.field_type ? (
                <div className="flex flex-col gap-0.5 py-1.5">
                  <dt className="text-xs font-medium text-[var(--g-secondary)]">
                    {t("pim.suggestions.drawer.fieldType")}
                  </dt>
                  <dd className="text-sm">{suggestion.payload.field_type}</dd>
                </div>
              ) : null}

              {!isPending && suggestion.reviewed_at ? (
                <div className="flex flex-col gap-0.5 py-1.5">
                  <dt className="text-xs font-medium text-[var(--g-secondary)]">
                    {t("pim.suggestions.drawer.reviewedAt")}
                  </dt>
                  <dd className="text-sm">{formatDate(suggestion.reviewed_at)}</dd>
                </div>
              ) : null}

              {!isPending && suggestion.decline_reason ? (
                <div className="flex flex-col gap-0.5 py-1.5">
                  <dt className="text-xs font-medium text-[var(--g-secondary)]">
                    {t("pim.suggestions.drawer.declineReason")}
                  </dt>
                  <dd className="whitespace-pre-wrap text-sm">{suggestion.decline_reason}</dd>
                </div>
              ) : null}
            </dl>

            {isPending ? (
              <div className="flex flex-wrap justify-end gap-2">
                <Button
                  variant="secondary"
                  size="md"
                  data-testid="suggestion-decline-btn"
                  onClick={() => setActionMode("decline")}
                  className="min-h-[44px] border-[var(--g-error)] text-[var(--g-error)] hover:bg-[var(--g-error)]/10"
                >
                  {t("pim.suggestions.drawer.decline")}
                </Button>
                <Button
                  variant="primary"
                  size="md"
                  data-testid="suggestion-approve-btn"
                  loading={approve.isPending}
                  onClick={handleApprove}
                  className="min-h-[44px]"
                >
                  {t("pim.suggestions.drawer.approve")}
                </Button>
              </div>
            ) : null}
          </div>
        )}
      </BottomSheet>

      {/* Decline reason — SIBLING BottomSheet, never nested. */}
      <BottomSheet
        open={actionMode === "decline"}
        onClose={() => setActionMode("none")}
        title={t("pim.suggestions.decline.title")}
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="suggestion-decline-reason" className="text-sm font-medium">
              {t("pim.suggestions.decline.label")}
            </label>
            <textarea
              id="suggestion-decline-reason"
              data-testid="suggestion-decline-reason-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t("pim.suggestions.decline.placeholder")}
              rows={4}
              maxLength={MAX_REASON}
              className="w-full resize-none rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)]"
              aria-describedby="suggestion-decline-reason-hint"
            />
            <p id="suggestion-decline-reason-hint" className="text-xs text-[var(--g-secondary)]">
              {t("pim.suggestions.decline.hint")} ({reason.length}/{MAX_REASON})
            </p>
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setActionMode("none")}
              disabled={decline.isPending}
              className="min-h-[44px]"
            >
              {t("pim.suggestions.decline.cancel")}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              data-testid="suggestion-decline-confirm-btn"
              onClick={() => void handleDecline()}
              disabled={!reasonValid || decline.isPending}
              className="min-h-[44px] border-[var(--g-error)] text-[var(--g-error)] hover:bg-[var(--g-error)]/10 disabled:opacity-50"
            >
              {t("pim.suggestions.decline.submit")}
            </Button>
          </div>
        </div>
      </BottomSheet>

      {/* Category-create form — SIBLING BottomSheet, never nested. Only
          rendered while a category-kind suggestion is loaded (its `open`
          otherwise stays false, gated by `actionMode`, anyway). */}
      {suggestion && suggestion.kind === "category" ? (
        <ApproveCategoryDialog
          suggestion={suggestion}
          open={actionMode === "approveCategory"}
          onClose={() => setActionMode("none")}
          onApproved={() => {
            setActionMode("none");
            onClose();
          }}
        />
      ) : null}
    </>
  );
}

export default SuggestionDrawer;
